import axios, { type AxiosRequestConfig, type AxiosResponse } from "axios";
import { Platform } from "react-native";

import { ENV_PREFIX, isLocal } from "@/config/env";

// Preview environments serve the FE at
// superclinical-fe-<repo>-pr-<n>.preview.curaihealth.com and the matching
// backend at superclinical-be-<repo>-pr-<n>.preview.curaihealth.com. Derive the
// backend host from the serving hostname so a single static bundle works for
// every PR (no per-PR host baked in at build time).
export const getPreviewBaseURL = (hostname: string): string | undefined => {
  if (!hostname.endsWith(".preview.curaihealth.com")) {
    return undefined;
  }
  return `https://${hostname.replace("superclinical-fe-", "superclinical-be-")}`;
};

export const getBaseURL = () => {
  if (Platform.OS === "web" && typeof window !== "undefined") {
    const previewBaseURL = getPreviewBaseURL(window.location.hostname);
    if (previewBaseURL) {
      return previewBaseURL;
    }
  }
  if (isLocal) {
    // Use environment variable for local development
    return process.env.EXPO_PUBLIC_SUPERCLINICAL_HOST;
  }
  // TODO: handle for native
  if (Platform.OS === "web") {
    const hostname = window.location.hostname;
    const envPrefix = ENV_PREFIX;
    if (hostname.includes("norihelps.com")) {
      return `https://api.${envPrefix}norihelps.com`;
    }
    if (hostname.includes("noricares.com")) {
      return `https://api.${envPrefix}noricares.com`;
    }
    if (hostname.includes("curaihealth.com")) {
      return `https://api.${envPrefix}curaihealth.com`;
    }
  }
};

const api = axios.create({
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  if (
    Platform.OS === "web" &&
    typeof window !== "undefined" &&
    !config.headers.has("X-Current-Url")
  ) {
    config.headers.set("X-Current-Url", window.location.href);
  }
  // Purposely not providing native support, we will solve for what we need to track for native when we get there, since native does not have urls.
  return config;
});

type ClientConfig<TData, TBody = unknown> = AxiosRequestConfig<TBody> & {
  returnType?: TData;
};
export const orvalClientRaw = <D, B = unknown>(
  config: ClientConfig<D, B>,
): Promise<AxiosResponse<D>> => {
  // Inject base URL dynamically to avoid Expo build errors
  if (!api.defaults.baseURL) {
    api.defaults.baseURL = getBaseURL();
  }
  return api.request<D>(config);
};

export const orvalClient = <D, B = unknown>(
  config: ClientConfig<D, B>,
): Promise<D> => orvalClientRaw<D, B>(config).then((res) => res.data);

export default api;
